import React, { useEffect, useState } from 'react';
import { View } from "react-native";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ExternalPriceProps } from "../../../store/types/sr_svc";
import moment from 'moment';
import Colors from '../../../constants/Colorsv2';
import { Text, TouchableOpacity } from '../../../globalComponents/NativeOverrides';
import { Icon } from 'react-native-elements';
import { TradeProps } from '../../../store/types/mk_svc';
import ActionModal from '../../../Componentsv1/ActionModal';
import { market_getOddsLabel } from '../helpers/markets';
import { order_calcAmericanOddsFromProbability } from '../helpers/orders';
import { useDispatch } from 'react-redux';
import { player_getHelpPromptById } from '../../../store/actionsv1/player';

type PriceHistoryCardProps = {
    external_prices:ExternalPriceProps[],
    trades: TradeProps[],
    width:number,
    level: 'freemium' | 'premium'
}

const color_options = [
    Colors.brand.cobalt,//BettorEdge
    Colors.highlights.highlight100,
    Colors.highlights.highlight200,
    Colors.highlights.highlight300,
    Colors.highlights.highlight400,
    Colors.highlights.highlight500
]

const PriceHistoryCard = ({ external_prices, trades, width, level } : PriceHistoryCardProps) => {
    const [ range, setRange ] = useState('hour');
    const [ books, setBooks ] = useState(['BettorEdge']);
    const [ range_select_visible, setRangeSelectVisible ] = useState(false);
    const [ book_select_visible, setBookSelectVisible ] = useState(false);
    const [ date_range, setDateRange ] = useState<string[]>([]);
    const unique_books = [ ...new Set(external_prices.map(p => p.external_name)) ]

    const dispatch = useDispatch();

    useEffect(() => {
        updateRange(range)
    },[range])

    const updateRange = (history:string) => {
        switch(history){
            case 'hour':
                let hr_start = moment().subtract(1, 'hour');
                let hr_range:string[] = []
                for (let i = 0; i < 12; i++) {
                    hr_range.push(hr_start.add(5, 'minutes').format('MM/DD/YYYY hh:mm a'))   
                }
                setDateRange(hr_range)
                return
            case 'day':
                let day_start = moment().subtract(1, 'day');
                let day_range:string[] = []
                for (let i = 0; i < 24; i++) {
                    day_range.push(day_start.add(1, 'hour').format('MM/DD/YYYY hh:mm a'))   
                }
                setDateRange(day_range)
                return
            case 'week':
                let week_start = moment().subtract(1, 'weeks');
                let week_range:string[] = []
                for (let i = 0; i < 14; i++) {
                    week_range.push(week_start.add(12, 'hours').format('MM/DD/YYYY hh:mm a'))   
                }
                setDateRange(week_range)
                return
            default:
                return
        }
    }

    let chart_data:any[] = []

    let min = 1;
    let max = 0;
    date_range.map(d => {
        let data:any = { name: 'Price', date: d }
        let trade = getClosestTrade(d, trades);
        if(trade){
            if(trade.probability < min){ min = trade.probability }
            if(trade.probability > max){ max = trade.probability }
            data['BettorEdge'] = Math.round(trade.probability*100*100)/100

        }
        
        let non_be_books = books.filter(b => b != 'BettorEdge')
        
        non_be_books.map(b => {
            let book_prices = external_prices.filter(p => p.external_name == b);
            let price = getClosestPrice(d, book_prices)
            if(price){
                if(price.probability < min){ min = price.probability }
                if(price.probability > max){ max = price.probability }
                data[b] = Math.round(price.probability*100*100)/100
                chart_data.push(data)
            }
        })
        chart_data.push(data)
    })

    let lower_buffer = (min*100) - 2
    let higher_buffer = (max*100) + 2

    
    return (
        <View style={{ width }}>
            <View style={{ flexDirection:'row', alignItems:'center', marginBottom:10, paddingRight:10, paddingLeft:10 }}>
                <Text style={{ flex:1 }} size={14} color={Colors.brand.midnight} weight='bold'>Chart</Text>
                <TouchableOpacity
                    style={{ flexDirection:'row', alignItems:'center', marginRight:5, padding:10, borderWidth:1, borderRadius:4, borderColor:Colors.shades.shade600 }}
                    onPress={() => setBookSelectVisible(true)}
                >
                    <Text style={{ marginRight:3 }} size={12} color={Colors.brand.midnight} weight='semibold'>BOOKS</Text>
                    <Icon name='chevron-down' type='feather' size={12} color={Colors.brand.midnight}/>
                </TouchableOpacity>
                <TouchableOpacity 
                    style={{ flexDirection:'row', alignItems:'center', padding:10, borderWidth:1, borderRadius:4, borderColor:Colors.shades.shade600 }}
                    onPress={() => setRangeSelectVisible(true)}
                    >
                    <Text style={{ marginRight:3 }} size={12} color={Colors.brand.midnight} weight='semibold'>{range.toUpperCase()}</Text>
                    <Icon name='chevron-down' type='feather' size={12} color={Colors.brand.midnight}/>
                </TouchableOpacity>
            </View>
            <ResponsiveContainer width={width} height={width*0.75}>
                <LineChart
                width={width}
                height={width*0.75}
                data={chart_data}
                margin={{
                    left:-40,
                    right:10,
                    bottom:10, 
                    top:10
                }}
                >
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis 
                    dataKey="date"
                    interval='preserveStartEnd'
                    tickFormatter={(value) => {
                        switch(range){
                            case 'hour':
                                return moment(value, 'MM/DD/YYYY hh:mm a').format('hh:mm a')
                            case 'day':
                                return moment(value, 'MM/DD/YYYY hh:mm a').format('h a')
                            case 'week':
                                return moment(value, 'MM/DD/YYYY hh:mm a').format('ddd')
                            default:
                                return value
                        }
                    }}
                    tick={{ fontSize:12, color:Colors.brand.midnight, fontFamily:'barlow-bold' }}
                    tickLine={false}
                    axisLine={false}
                />
                <YAxis 
                    tickLine={false}
                    tick={{ fontSize:12, color:Colors.brand.midnight, fontFamily:'barlow-bold' }}
                    interval={'preserveStartEnd'}
                    axisLine={false}
                    tickMargin={-30}
                    tickFormatter={(value) => `${market_getOddsLabel(order_calcAmericanOddsFromProbability(value/100))}`}
                    domain={[lower_buffer, higher_buffer]}
                />
                <Tooltip 
                        wrapperStyle={{ outline: 'none' }}
                        cursor={false}
                        content={<CustomTooltip />}
                    />
                <Legend 
                    content={<CustomLegend width={width - 40}/>}
                />
                {books.map((b,i) => {
                    let color = color_options[i]
                    return (
                        <Line type='linear' strokeWidth={2} dataKey={b} stroke={color}  activeDot={{ r: 4 }} />
                    )
                })}
                </LineChart>
            </ResponsiveContainer>
            <ActionModal
                isVisible={range_select_visible}
                label_attribute='label'
                closeModal={() => setRangeSelectVisible(false)}
                options={[
                    { label: 'Last Hour', range: 'hour' },
                    { label: 'Last Day', range: 'day' },
                    { label: 'Last Week', range: 'week' },
                ]}
                onSelectOption={(option) => {
                    setRange(option.range)
                }}
            />
            <ActionModal
                isVisible={book_select_visible}
                multi
                selected={books.map(b => {
                    return { label: b, book:b }
                })}
                locked={level != 'premium'?true:false}
                label_attribute='label'
                closeModal={() => setBookSelectVisible(false)}
                options={unique_books.map(b => {
                    return { label: b, book:b }
                })}
                onSelectOption={(option) => {
                    if(level != 'premium'){
                        setBookSelectVisible(false);
                        return dispatch(player_getHelpPromptById('t2_analytics_3'))
                    }
                    let exists = books.find(b => b == option.book)
                    if(exists){ setBooks(books.filter(b => b != option.book)) }
                    else { setBooks(books.concat(option.book)) }
                }}
            />
        </View>
    )
}

const getClosestTrade = (date:string, trades:TradeProps[]):TradeProps|undefined => {
    let nearest_trade:TradeProps | undefined;
    trades.map(t => {
        let diff = moment(t.create_datetime).diff(moment(date), 'minutes')
        if(diff < 0){            
            if(nearest_trade){
                if(moment(t.create_datetime).diff(moment(nearest_trade.create_datetime)) > 0){
                    nearest_trade = t
                }
            } else {
                nearest_trade = t
            }
        }
    })
    return nearest_trade
}

const getClosestPrice = (date:string, prices:ExternalPriceProps[]):ExternalPriceProps|undefined => {
    
    let nearest_trade:ExternalPriceProps | undefined;
    prices.map(t => {
        let diff = moment(t.create_datetime).diff(moment(date), 'minutes')
        if(diff < 0){            
            if(nearest_trade){
                if(moment(t.create_datetime).diff(moment(nearest_trade.create_datetime)) > 0){
                    nearest_trade = t
                }
            } else {
                nearest_trade = t
            }
        }
    })
    return nearest_trade
}


const CustomTooltip = ({ active, payload}:any) => {
    if(!active || !payload || !payload.length){ return <></> }
    let data = payload[0]?.payload
    if(!data){ return <></> }
    return (
        <View style={{ padding:5, minWidth: 150, borderRadius:4, backgroundColor:Colors.shades.white, ...local_styles.raised }}>
            <Text size={12} color={Colors.brand.midnight} weight='semibold' textAlign='right'>{data.date}</Text>
            <View style={{ marginTop:5 }}>
                {payload.map(p => {
                    return (
                        <View style={{ flexDirection:'row', marginTop:5 }}>
                            <Text style={{ flex:2/3 }} size={12} color={p.color} weight='regular'>{p.name}</Text>
                            <Text style={{ flex:1 }} size={12} color={p.color} weight='bold' textAlign='right'>{p.value}% ({market_getOddsLabel(order_calcAmericanOddsFromProbability(p.value/100))})</Text>
                        </View>
                    )
                })}
            </View>
        </View>
    );
  };


const CustomLegend = ({ payload, width }:any) => {
    const [ collapsed, setCollapsed ] = useState(true);
    return (
        <View style={{ position: 'absolute',  bottom:10, left:50, alignItems:'flex-start' }}>
            <View style={{ flex:1, maxWidth:width, flexDirection:'row', padding:5, borderRadius:4, backgroundColor:Colors.shades.white, ...local_styles.raised }}>
                <TouchableOpacity style={{ padding:10, alignSelf:'flex-start' }} onPress={() => setCollapsed(!collapsed)}>
                    {!collapsed ?
                    <Icon name='minimize-2' type='feather' color={Colors.brand.electric} size={12}/>
                    :
                    <Icon name='maximize-2' type='feather' color={Colors.brand.electric} size={12}/>
                    }
                </TouchableOpacity>
                {!collapsed ?
                <View nativeID='legend_items' style={{ flex:1, flexDirection:'row', flexWrap:'wrap' }}>
                    {payload.map((p:any) => {
                        return (
                            <View>
                                <View style={{ flexDirection:'row', padding:5, alignItems:'center' }}>
                                <View style={{ height:10, width:10, backgroundColor:p.color, borderRadius:100, marginRight:2 }} />
                                <Text style={{ flex:1 }} size={12} color={Colors.brand.midnight} weight='regular'>{p.value}</Text>
                            </View>
                            </View>
                        )
                    })}
                </View>
                :<></>}
            </View>
        </View>
    )
}

const local_styles = {
    raised: { shadowColor: "rgba(0, 0, 0, 0.12)", shadowOffset: { width: 0, height: 10 }, shadowRadius: 10, shadowOpacity: 1 }
}

const areEqual = (left:PriceHistoryCardProps, right:PriceHistoryCardProps) => {
    if(left.external_prices.length != right.external_prices.length){ return false }
    const left_side = left.external_prices[0]
    const right_side = right.external_prices[0]
    if(!left_side && !right_side){ return true }
    if(left_side && !right_side){ return false }
    if(right_side && !left_side){ return false }
    const left_event = `${left_side.event_id}:${left_side.event_type}:${left_side.market_id}:${left_side.participant_id}`
    const right_event = `${right_side.event_id}:${right_side.event_type}:${right_side.market_id}:${right_side.participant_id}`

    if(left_event != right_event){ return false }
    return true
}

export default React.memo(PriceHistoryCard, areEqual)